import { Inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, tap, switchMap, catchError } from 'rxjs/operators';
import { LoginModel } from '../shared/login.model';
import { environment } from '../../../environments/environment';
import { Role, UserAccountClient, UserInfoDTO } from '../models/models.swagger.generated';
import { TranslateService } from '@ngx-translate/core';
import { LoginProxy } from '../http/login-proxy.service';
import { Idle } from '@ng-idle/core';
import { DOCUMENT } from '@angular/common';
import { Settings } from 'luxon';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  public redirectUrl: string;


  private userInfoDTO: UserInfoDTO;
  private _isAdmin = false;
  private _isClientAdmin = false;


  constructor(
    private loginProxy: LoginProxy,
    private userAccountClient: UserAccountClient,
    private readonly translate: TranslateService,
    private idle: Idle,
    @Inject(DOCUMENT) private document: Document) {
    this.resetLocalStorageIfNecessary();
    // Reset user info from the local storage
    if (localStorage[environment.localStorageUserInfo]) {
      this.userInfoDTO = UserInfoDTO.fromJS(JSON.parse(localStorage[environment.localStorageUserInfo]));
      this.setClientLanguage(this.userInfoDTO.language);
      setTimeout(() => this.idle.watch(), 1000);
      setTimeout(() => this.loadUserInfoDTO(), 1000);
    }
  }

  public login(login: LoginModel): Observable<string> {
    return this.loginProxy.login(login.username, login.password).pipe(
      tap(([token, refreshToken]) => {

        if (token) {

          localStorage[environment.localStorageToken] = token;

          this.idle.watch();
          this.updateActivityTimeStamp();
        }
        if (refreshToken) {
          localStorage[environment.localStorageRefreshToken] = refreshToken;
        }
      }),
      switchMap(() => {

        return this.userAccountClient.info().pipe(
          catchError((error) => {
            this.resetLocalStorage();
            throw error;
          })
        );
      }),
      map((userInfo: UserInfoDTO) => {
        this.setUserInfoDTO(userInfo);
        return localStorage[environment.localStorageToken];
      })
    );
  }

  public logout(): void {
    this.userAccountClient.logout().subscribe();
    setTimeout(() => {
      this.resetLocalStorage();
      location.reload();
    }, 0);
  }

  public setUserLanguage(language: string) {
    const userInfo = this.getUserInfo();
    if (userInfo == null) {
      this.setClientLanguage(language);
      return;
    }
    userInfo.language = language;
    this.setUserInfoDTO(userInfo);
    this.userAccountClient.setUserLanguage(language).subscribe();
  }

  public isUserLoggedIn(): boolean {
    if (localStorage[environment.localStorageToken]) {
      return true;
    } else {
      return false;
    }
  }

  public isUserAdmin(): boolean {



    return this._isAdmin;
  }

  public isUserAdminOrClientAdmin(): boolean {

    return this._isAdmin || this._isClientAdmin;
  }
  public get isAdmin(): boolean {
    return this._isAdmin;
  }

  public get isClientAdmin(): boolean {
    return this._isClientAdmin;
  }




  public hasUserClientAssigned(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }
    return !!this.userInfoDTO.client;
  }

  public getUserInfo(): UserInfoDTO {
    if (!this.isUserLoggedIn() || !localStorage[environment.localStorageUserInfo]) {
      return null;
    }

    if (!this.userInfoDTO) {
      this.userInfoDTO = UserInfoDTO.fromJS(JSON.parse(localStorage[environment.localStorageUserInfo]));
    }

    return this.userInfoDTO;
  }
  public getDisplayName(): string {
    const user = this.getUserInfo();
    if (!user) {
      return '';
    }

    const first = user.firstname?.trim();
    const last = user.lastname?.trim();


    if (first || last) {
      return `${first || ''} ${last || ''}`.trim();
    }


    return user.userName || user.email || '';
  }



  public loadUserInfoDTO(): void {
    this.userAccountClient.info().subscribe(userInfo => {
      this.setUserInfoDTO(userInfo);
    });
  }

  private setUserInfoDTO(userInfo: UserInfoDTO) {
    if (!userInfo) {
      return;
    }
    this.userInfoDTO = userInfo;
    localStorage[environment.localStorageUserInfo] = JSON.stringify(userInfo);
    this._isAdmin = userInfo.roles?.includes(Role.Admin);
    this._isClientAdmin = userInfo.roles?.includes(Role.ClientAdmin);
    this.setClientLanguage(userInfo.language);
  }

  public updateActivityTimeStamp(): void {
    if (this.isUserLoggedIn()) {
      localStorage[environment.localStorageActivityTimestamp] = Date.now;
    }
  }

  private setClientLanguage(language: string) {
    const supportedLanguages = environment.supportedLanguages?.split(',').filter(s => s?.length > 0);
    if (!supportedLanguages) {
      console.error('no supported languages defined!');
      return;
    }
    if (!language) {
      language = supportedLanguages[0];
      if (this.userInfoDTO) {
        this.userInfoDTO.language = language;
      }
    }
    Settings.defaultLocale = language;
    this.translate.use(language);
    if (environment.allowBrowserTranslation) {
      this.document.documentElement.lang = language;
    } else {
      this.document.documentElement.translate = false;
    }
  }
  public isUserClientAdmin(): boolean {
    if (!this.isUserLoggedIn()) {
      return false;
    }

    if (!this.userInfoDTO || !this.userInfoDTO.roles) {
      return false;
    }

    return this.userInfoDTO.roles.indexOf(Role.ClientAdmin) > -1;
  }




  private resetLocalStorageIfNecessary() {
    if (environment.production) {
      const timestamp = localStorage[environment.localStorageActivityTimestamp];
      if (!timestamp || timestamp + (environment.idleTimeoutInSeconds * 1000) < Date.now) {
        this.resetLocalStorage();
      }
    }

    // avoid possible dead lock
    if ((localStorage[environment.localStorageUserInfo] && !localStorage[environment.localStorageToken]) || (!localStorage[environment.localStorageUserInfo] && localStorage[environment.localStorageToken])) {
      this.resetLocalStorage();
    }
  }

  private resetLocalStorage() {
    localStorage.removeItem(environment.localStorageToken);
    localStorage.removeItem(environment.localStorageRefreshToken);
    localStorage.removeItem(environment.localStorageUserInfo);
  }
}
